import { HttpErrorResponse, HttpInterceptorFn, HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { BehaviorSubject, throwError, Observable, from, of } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { ToastService } from '../services/toast.service';
import { AuthService } from '../services/auth.service';
import { Router } from '@angular/router';

/** Marca las peticiones ya reintentadas tras refrescar, para no reintentar en bucle. */
const RETRY_HEADER = 'X-Retried-After-Refresh';

let isRefreshing = false;
const parseJson = (text: string): unknown => {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const STATUS_MESSAGES: Record<number, string> = {
  0: 'No se pudo conectar con el servidor. Revisa tu conexión a internet e inténtalo de nuevo.',
  400: 'No se pudo completar la acción. Revisa los datos e inténtalo de nuevo.',
  403: 'No tienes permiso para realizar esta acción.',
  404: 'No se encontró lo que buscas.',
  408: 'El servidor tardó demasiado en responder. Inténtalo de nuevo.',
  409: 'Esta acción entra en conflicto con información existente.',
  413: 'El archivo es demasiado grande. Usa uno más liviano.',
  429: 'Demasiados intentos. Espera un momento e inténtalo de nuevo.',
  504: 'El servidor tardó demasiado en responder. Inténtalo de nuevo.',
};

/**
 * Texto para el usuario. Si la API mandó un mensaje propio (ya está en español y pensado para mostrarse) se usa; si no, uno por
 * defecto según el estado. Nunca se muestra el texto técnico de Angular ("Http failure response for ...").
 */
function friendlyErrorMessage(error: any): string {
  const apiMsg = error?.error?.message;
  const fromApi = Array.isArray(apiMsg)
    ? apiMsg.map((m: any) => (typeof m === 'string' ? m : Object.values(m?.constraints || {}).join(', '))).filter(Boolean).join('. ')
    : typeof apiMsg === 'string'
      ? apiMsg
      : '';
  if (fromApi) return fromApi;
  const status = Number(error?.status);
  return STATUS_MESSAGES[status] ?? (status >= 500 ? 'Ocurrió un problema en el servidor. Inténtalo de nuevo en unos minutos.' : 'Ha ocurrido un error inesperado. Inténtalo de nuevo.');
}

/** Peso legible: "212 KB" o "3.1 MB". */
const readable = (bytes: number) => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

/**
 * Si el envío lleva un archivo y el servidor lo corta (sin respuesta o 413: un proxy que rechaza cuerpos grandes no manda cabeceras CORS y el navegador
 * solo ve "Failed to fetch"), el aviso dice cuánto pesaba el archivo en vez de un error genérico de conexión.
 */
function uploadTooBigMessage(req: HttpRequest<unknown>, error: any): string | null {
  if (!(req.body instanceof FormData) || ![0, 413].includes(Number(error?.status))) return null;
  const file = [...req.body.values()].find((v): v is File => v instanceof File);
  if (!file) return null;
  return `No se pudo subir "${file.name}" (${readable(file.size)}): el servidor la rechazó por su tamaño. Usa una imagen más liviana (menos de 2 MB).`;
}

const refreshTokenSubject = new BehaviorSubject<string | null>(null);

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const toastService = inject(ToastService);
  const authService = inject(AuthService);
  const router = inject(Router);

  // Si la request tiene el header X-Silent-Error, no mostrar toast de error
  const isSilent = req.headers.has('X-Silent-Error');
  // Marca interna: la petición ya se reintentó tras refrescar el token.
  const isRetry = req.headers.has(RETRY_HEADER);
  // Clonar sin los headers internos antes de enviar
  let cleanReq = isSilent ? req.clone({ headers: req.headers.delete('X-Silent-Error') }) : req;
  cleanReq = isRetry ? cleanReq.clone({ headers: cleanReq.headers.delete(RETRY_HEADER) }) : cleanReq;

  return next(cleanReq).pipe(
    catchError((error) => {
      // Si el error es 401 Unauthorized y no es del login o refresh.
      //
      // isRetry corta el bucle: si la petición ya se reintentó con un token
      // recién refrescado y vuelve a dar 401, el token no era el problema.
      // Sin esto se refrescaba una y otra vez, y la rotación repetida del
      // refresh token acababa matando la sesión.
      if (
        error.status === 401 &&
        !isRetry &&
        !req.url.includes('/auth/login') &&
        !req.url.includes('/auth/refresh')
      ) {
        return handle401Error(cleanReq, next, authService, router, toastService);
      }

      // Las descargas (blob) traen el error como Blob: se lee el JSON para que quien llama use `err.error.message`, y no se muestra
      // un aviso genérico aquí, porque quien descarga ya avisa al usuario (antes salía duplicado y con el texto técnico de Angular).
      if (error instanceof HttpErrorResponse && error.error instanceof Blob) {
        return from(error.error.text()).pipe(
          catchError(() => of('')),
          switchMap((text) =>
            throwError(
              () =>
                new HttpErrorResponse({
                  error: parseJson(text),
                  headers: error.headers,
                  status: error.status,
                  statusText: error.statusText,
                  url: error.url ?? undefined,
                }),
            ),
          ),
        );
      }

      // Archivo rechazado por tamaño: el mismo mensaje llega al aviso y a quien llama (err.error.message), con estado 413.
      const tooBig = uploadTooBigMessage(req, error);
      if (tooBig) {
        error = new HttpErrorResponse({ error: { message: tooBig }, headers: error.headers, status: 413, statusText: 'Payload Too Large', url: error.url ?? undefined });
      }

      // Evitar mostrar toast genérico para errores de autenticación, refresco o 401
      const isAuthOrRefresh = req.url.includes('/auth/login') || req.url.includes('/auth/refresh') || error.status === 401;
      if (!isSilent && !isAuthOrRefresh) {
        toastService.show('Error', friendlyErrorMessage(error), 'error', 4000);
      }

      return throwError(() => error);
    })
  );
};

function handle401Error(
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  authService: AuthService,
  router: Router,
  toastService: ToastService
): Observable<any> {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    // El catchError va ANTES del switchMap: solo cuenta lo que falle el refresh, no el reintento de la petición original
    // (un 403 de permisos o un 500 de esa petición no significa que la sesión haya terminado).
    return authService.refreshToken().pipe(
      catchError((refreshErr) => {
        isRefreshing = false;
        // Solo se cierra la sesión si el servidor RECHAZÓ el refresh (401/403: caducó, se revocó o se reutilizó). Un corte de red,
        // un 502 o un timeout no dicen nada de la sesión: antes sacaban al usuario aunque su sesión siguiera válida.
        const rejected = [400, 401, 403].includes(refreshErr?.status); // 400: ni siquiera había refresh token que enviar
        // Avisa a las peticiones que esperaban este refresh para que no queden colgadas.
        refreshTokenSubject.next('');
        if (!rejected) return throwError(() => refreshErr);

        authService.logout();

        // Evitar duplicar el toast si ya estamos en la pantalla de login
        if (!router.url.includes('/auth/login')) {
          toastService.show(
            'Sesión Expirada',
            'Tu sesión expiró. Inicia sesión de nuevo.',
            'warning',
            4000
          );
        }

        router.navigate(['/auth/login']);
        return throwError(() => refreshErr);
      }),
      switchMap((res: any) => {
        isRefreshing = false;
        const newToken = res.accessToken || res.token;
        refreshTokenSubject.next(newToken);

        return next(
          request.clone({
            setHeaders: {
              Authorization: `Bearer ${newToken}`,
              [RETRY_HEADER]: '1'
            }
          })
        );
      })
    );
  } else {
    return refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => {
        // '' = el refresh falló: esta petición también falla (sin reintentar) en vez de quedarse esperando.
        if (!token) return throwError(() => new HttpErrorResponse({ status: 401, url: request.url }));
        return next(
          request.clone({
            setHeaders: {
              Authorization: `Bearer ${token}`,
              [RETRY_HEADER]: '1'
            }
          })
        );
      })
    );
  }
}
